/**
 * OC Chat Widget Component
 * Main entry point - handles authentication state and theme configuration
 */

import { Alert } from '@hl/ui-components';
import { FC, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { GlobalStyleWrapper } from '../../styles/GlobalStyle';
import { IAppTheme } from '../../styles/IAppTheme';
import { OcChatConfig, OcWidgetOptions } from '../../types/oc';
import OcAiChatSkeleton from '../Loading/AiChatSkeleton';
import { ErrorWrapper } from '../TranscriptBox/TranscriptMessages.styled';
import { OcChat } from './components/Chat/OcChat';
import { OcLoginRequired } from './components/LoginRequired/OcLoginRequired';
import {
  ConfigValidationError,
  validateOcChatConfig,
} from './config/configValidator';
import { isAuthError } from './config/ocDefaults';
import { OC_DARK_THEME, OC_DEFAULT_THEME } from './config/ocTheme';
import { OcChatWidgetContainer } from './OcChatWidget.styled';
import { checkOcAuth } from './services/ocEndpoints';

export interface OcChatWidgetProps {
  config: OcChatConfig;
  options?: OcWidgetOptions;
}

type AuthState = 'checking' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * OcChatWidget Component
 * Handles authentication state and routes to appropriate UI
 */
export const OcChatWidget: FC<OcChatWidgetProps> = ({ config, options }) => {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [userId, setUserId] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Validate config before attempting authentication
        // throws an error if the config is invalid which is caught and displayed to the user
        validateOcChatConfig(config);

        const authResponse = await checkOcAuth(config);

        if (authResponse.isAuthenticated) {
          setUserId(authResponse.userId || '');
          setAuthState('authenticated');

          if (options?.onReady) {
            // Defer to next tick to ensure render is complete
            setTimeout(options.onReady, 0);
          }
        } else {
          setAuthState('unauthenticated');

          if (options?.onAuthRequired) {
            options.onAuthRequired();
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setAuthState('error');

        if (options?.onError) {
          options.onError(error);
        }
      }
    };

    checkAuth();
  }, [config, options]);

  // Build theme: start with OC defaults, then apply custom overrides
  const themeMode = options?.themeMode || 'light';
  const baseTheme = themeMode === 'dark' ? OC_DARK_THEME : OC_DEFAULT_THEME;

  const theme: IAppTheme = options?.theme
    ? {
        ...baseTheme,
        color: {
          ...baseTheme.color,
          ...options.theme.color,
        },
        font: {
          size: {
            ...baseTheme.font.size,
            ...options.theme.font?.size,
          },
          weight: {
            ...baseTheme.font.weight,
            ...options.theme.font?.weight,
          },
        },
      }
    : baseTheme;

  // Checking authentication
  if (authState === 'checking') {
    return (
      <>
        <GlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcChatWidgetContainer>
            <OcAiChatSkeleton />
          </OcChatWidgetContainer>
        </ThemeProvider>
      </>
    );
  }

  // Not authenticated, or login error - show login button
  if (
    authState === 'unauthenticated' ||
    (authState === 'error' && error && isAuthError(error))
  ) {
    return (
      <>
        <GlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcChatWidgetContainer>
            <OcLoginRequired
              loginUrl={config.loginUrl}
              title={options?.loginTitle}
              message={options?.loginHelpText}
              buttonText={options?.loginButtonText}
            />
          </OcChatWidgetContainer>
        </ThemeProvider>
      </>
    );
  }

  // Error state
  if (authState === 'error' && error) {
    const isConfigError = error instanceof ConfigValidationError;
    const errorMessage = isConfigError
      ? `Configuration Error: ${error.message}`
      : 'An error occurred. Reloading the page may fix the issue.';

    return (
      <>
        <GlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcChatWidgetContainer>
            <ErrorWrapper>
              {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
              <Alert danger={true}>{errorMessage}</Alert>
            </ErrorWrapper>
          </OcChatWidgetContainer>
        </ThemeProvider>
      </>
    );
  }

  // Authenticated - render chat with all providers
  return (
    <>
      <GlobalStyleWrapper />
      <OcChatWidgetContainer>
        <OcChat config={config} userId={userId} theme={theme} options={options} />
      </OcChatWidgetContainer>
    </>
  );
};

export default OcChatWidget;
